import { useEffect, useState } from "react";
import axios from "axios";
import { Wand2, BookmarkCheck } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { EmptyState } from "@/components/admin/ui";

const TABS = [
  { id: "adhoc", label: "Ad Hoc", icon: Wand2 },
  { id: "saved", label: "Saved", icon: BookmarkCheck },
];

const SCOPE_LABEL = {
  org: "your organization",
  team: "the team(s) you lead",
  self: "you",
};

export default function OrgAnalytics() {
  const { API } = useAuth();
  const [tab, setTab] = useState("adhoc");
  const [scope, setScope] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/org/analytics/scope`, { withCredentials: true })
      .then(({ data }) => setScope(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API]);

  return (
    <div className="space-y-6" data-testid="org-analytics-page">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-950 dark:text-white">Analytics</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {scope ? `Scoped to ${SCOPE_LABEL[scope.level] || "you"}.` : "Build and save reports across your organization."}
        </p>
      </div>

      {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-analytics-error">{error}</p>}

      <div className="flex gap-1 rounded-xl bg-slate-100 p-1 dark:bg-white/5" data-testid="org-analytics-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`org-analytics-tab-${t.id}`}
              className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-bold transition-colors duration-200 ${
                tab === t.id
                  ? "bg-white text-[#0052FF] shadow-sm dark:bg-[#111C33] dark:text-blue-300"
                  : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              }`}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 dark:border-white/15 dark:bg-white/[0.03]" data-testid={`org-analytics-panel-${tab}`}>
        {tab === "adhoc" ? (
          <EmptyState
            icon={Wand2}
            title="Ad Hoc analytics is coming soon."
            message="Build one-off queries and reports on demand, scoped to your organization, team, or role."
            testId="org-analytics-adhoc-empty"
          />
        ) : (
          <EmptyState
            icon={BookmarkCheck}
            title="Saved analytics is coming soon."
            message="Reports you save for reuse will show up here, scoped the same way as Ad Hoc."
            testId="org-analytics-saved-empty"
          />
        )}
      </div>
    </div>
  );
}
